import { attachRouterTracing } from './shared/lib/sentry';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import { registerOnLogout, useAuthStore } from '@/shared/model';
import { initAuthCrossTabSync } from '@/shared/model/authCrossTabSync';
import { startVersionCheck } from '@/shared/lib/version-check/checkVersion';
import {
  attachPageViewTracking,
  attachWindowViewTracking,
} from '@/shared/lib/page-view/trackPageView';
import { useWindowStore } from '@/shared/model/windowStore';
import { initInstallPromptCapture } from '@/shared/lib/pwa/installPrompt';
import { attachAuthCacheReset } from '@/shared/api';
import { attachAssistantAuthReset } from '@/features/assistant/model/attachAssistantAuthReset';

// sentry.ts 는 router 를 static import 하지 않는다(전 페이지 그래프가 딸려오므로) —
// 라우터 트레이싱 통합만 여기서 주입한다. sentry init 자체는 위 import 시점에 이미 끝났다.
attachRouterTracing(router);

registerOnLogout(() => void router.navigate({ to: '/login' }));

// AXIOM-WEB-2 Tier 4-B: auth user transition 시 queryClient.clear() 자동 발화
attachAuthCacheReset();

// 계정 전환·로그아웃 시 도깨비 persist localStorage(unread·graph view) 초기화.
// queryClient.clear()는 react-query 캐시만 비우므로 zustand persist는 여기서 따로 비운다.
attachAssistantAuthReset();

// 탭 간 로그아웃 동기화
initAuthCrossTabSync(() => useAuthStore.getState().clearAuth());

// 새 deploy 감지 — 한 페이지에 머무는 사용자도 배너로 인지
startVersionCheck();

// 페이지 사용량 측정 — 라우트 진입을 라우트 패턴 단위로 기록
attachPageViewTracking(router);

// 창 사용량 측정 — 창 메타포라 라우트가 안 바뀌므로 창 열림을 window:{id} 로 기록
attachWindowViewTracking(useWindowStore);

// beforeinstallprompt 는 문서당 1회만 발화한다. 로그인 화면에 머무는 동안 발화해도
// 놓치지 않도록 React 마운트 전에 캡처해 둔다 (InstallPrompt 가 스냅샷을 구독)
initInstallPromptCapture();

// 부팅 시 서버 세션 검증은 routes/__root.tsx의 beforeLoad에서 수행한다
import { DeploymentBanner } from '@/shared/ui/DeploymentBanner';
import { ErrorBoundary } from './shared/ui/atoms/ErrorBoundary/ErrorBoundary';
import './shared/ui/fonts/fonts.css';
import './shared/ui/theme/orv-theme.css';
import './shared/ui/theme/default-theme-override.css';
import './shared/ui/theme/motifs';
import './app/styles/global.css';

const container = document.getElementById('app');

if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

// RouterProvider 바깥의 최후 그물 — __root.tsx 의 ErrorBoundary 는 <Outlet/> 안쪽이라
// 라우터 자체가 던진 값은 놓친다. TanStack 의 CatchBoundary 도 `error ? fallback : children`
// 판정이라 falsy(`throw undefined`)를 흘려보내고, 그러면 React 가 트리를 버려 백지가 된다
// (Sentry AXIOM-WEB-24, Replay 확인). 라우터가 죽은 상태라 복구는 리로드뿐이다.
root.render(
  <StrictMode>
    <DeploymentBanner />
    <ErrorBoundary onRetry={() => window.location.reload()}>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>
);
